"use client";

import dynamic from "next/dynamic";
import { useState } from "react";
import SquarePen from "lucide-react/dist/esm/icons/square-pen";
import PlusCircle from "lucide-react/dist/esm/icons/circle-plus";
import Store from "lucide-react/dist/esm/icons/store";
import BarChart3 from "lucide-react/dist/esm/icons/chart-bar";
import ExternalLink from "lucide-react/dist/esm/icons/external-link";
import UserCog from "lucide-react/dist/esm/icons/user-cog";
import { useProfile } from "@/contexts/ProfileContext";
import { ContextActionMenu, type ContextAction } from "./ContextActionMenu";

// The admin detail panel + moderation/delete dialogs are heavy and load only
// after the admin gate, so none of it ships in the public profile bundle for
// ordinary visitors. Same approach as ProfileAdminControls.
const UserAdminDialogs = dynamic(
  () => import("./UserAdminDialogs").then((m) => m.UserAdminDialogs),
  { ssr: false },
);

interface UserContextActionsProps {
  /** Profile being viewed. */
  profileId: string | null | undefined;
  /** Whether the viewer owns this profile. */
  isOwnProfile: boolean;
  /** True on a seller store page — unlocks the seller-hub shortcuts. */
  isSeller?: boolean;
  className?: string;
}

/**
 * Context actions for a person: their own edit shortcuts, plus the full admin
 * moderation surface for staff — offered on the page you're already on rather
 * than sending you to /admin or /seller to find the same row again.
 */
export function UserContextActions({
  profileId,
  isOwnProfile,
  isSeller = false,
  className,
}: UserContextActionsProps) {
  const { isAdmin } = useProfile();
  const [adminPanelOpen, setAdminPanelOpen] = useState(false);

  const ownerActions: ContextAction[] = [];
  if (isOwnProfile) {
    ownerActions.push({
      id: "edit-profile",
      label: "Edit profile",
      icon: SquarePen,
      href: "/dashboard/settings",
    });
    if (isSeller) {
      ownerActions.push(
        { id: "edit-store", label: "Edit store profile", icon: Store, href: "/seller/store-profile" },
        { id: "new-asset", label: "New asset", icon: PlusCircle, href: "/seller/assets/new" },
        { id: "analytics", label: "Store analytics", icon: BarChart3, href: "/seller/analytics" },
      );
    }
  }

  const adminActions: ContextAction[] = [];
  if (isAdmin && profileId) {
    adminActions.push(
      {
        id: "manage-user",
        label: "Manage user",
        description: "Moderate, restrict, inspect",
        icon: UserCog,
        onSelect: () => setAdminPanelOpen(true),
      },
      {
        id: "open-in-admin",
        label: "Open in Users hub",
        icon: ExternalLink,
        href: `/admin/users?user=${profileId}`,
      },
    );
  }

  return (
    <>
      <ContextActionMenu
        ownerActions={ownerActions}
        adminActions={adminActions}
        label={isOwnProfile ? "Manage" : "Admin"}
        className={className}
        triggerAriaLabel="Actions for this profile"
      />
      {/* Mounted only once an admin actually opens it, so the dialog bundle is
          fetched on demand rather than on every profile view. */}
      {isAdmin && profileId && adminPanelOpen && (
        <UserAdminDialogs
          profileId={profileId}
          open={adminPanelOpen}
          onOpenChange={setAdminPanelOpen}
        />
      )}
    </>
  );
}

export default UserContextActions;
